import React from 'react';
import { Link } from 'react-router-dom';
import './Terms.css';

const Terms = () => {
  React.useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="legal-page" id="main">
      <div className="legal-container">
        <div className="legal-header">
          <h1>Terms of Service</h1>
          <p className="last-updated">Last updated: December 2024</p>
        </div>

        <div className="legal-content">
          <section>
            <h2>1. Acceptance of Terms</h2>
            <p>
              By accessing and using the services provided by ShawnyMedia LLC ("we," "our," or "us"), you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service. These terms apply to all users of our website and services.
            </p>
          </section>

          <section>
            <h2>2. Description of Service</h2>
            <p>
              ShawnyMedia LLC provides digital marketing, web development, graphic design, and video production services. Our services include but are not limited to:
            </p>
            <ul>
              <li>Social media marketing and management</li>
              <li>Website design and development</li>
              <li>Graphic design and branding</li>
              <li>Video production and editing</li>
              <li>Digital marketing consultation</li>
              <li>Content creation and strategy</li>
              <li>SEO and search engine optimization</li>
            </ul>
          </section>

          <section>
            <h2>3. User Responsibilities</h2>
            <p>As a user of our services, you agree to:</p>
            <ul>
              <li>Provide accurate and complete information when requested</li>
              <li>Maintain the security of any account credentials</li>
              <li>Use our services only for lawful purposes</li>
              <li>Not interfere with or disrupt our services</li>
              <li>Respect intellectual property rights</li>
              <li>Not attempt to gain unauthorized access to our systems</li>
              <li>Comply with all applicable laws and regulations</li>
            </ul>
          </section>

          <section>
            <h2>4. Payment Terms</h2>
            <h3>4.1 Pricing</h3>
            <p>
              All pricing for our services will be clearly communicated before project commencement. Prices are subject to change with appropriate notice. All prices are quoted in US dollars unless otherwise specified.
            </p>

            <h3>4.2 Payment Schedule</h3>
            <p>
              Payment terms will be outlined in individual project agreements. Typically, we require a deposit before work begins, with remaining balance due upon project completion. Payment methods accepted include bank transfers, credit cards, and other agreed-upon methods.
            </p>

            <h3>4.3 Late Payments</h3>
            <p>
              Late payments may result in suspension of services and may incur additional fees as outlined in project agreements. Interest may be charged on overdue amounts at a rate of 1.5% per month.
            </p>
          </section>

          <section>
            <h2>5. Project Terms</h2>
            <h3>5.1 Project Scope</h3>
            <p>
              Project scope, deliverables, and timelines will be clearly defined in project agreements. Any changes to scope may result in additional charges and timeline adjustments.
            </p>

            <h3>5.2 Revisions</h3>
            <p>
              We include a reasonable number of revisions in our project pricing. Additional revisions may incur additional charges. Revisions must be requested within 30 days of project completion.
            </p>

            <h3>5.3 Project Completion</h3>
            <p>
              Projects are considered complete when all deliverables have been provided and approved by the client, or when the client has not provided feedback within the specified timeframe (typically 14 days).
            </p>
          </section>

          <section>
            <h2>6. Intellectual Property</h2>
            <h3>6.1 Client Materials</h3>
            <p>
              Clients retain ownership of materials they provide to us. By providing materials, clients grant us license to use them for project purposes. Clients warrant that they have the right to provide such materials.
            </p>

            <h3>6.2 Our Work</h3>
            <p>
              Upon full payment, clients receive rights to use the final deliverables as specified in project agreements. We retain the right to showcase work in our portfolio and marketing materials.
            </p>

            <h3>6.3 Third-Party Materials</h3>
            <p>
              Any third-party materials used in projects (fonts, stock photos, etc.) are subject to their respective licenses and terms. Clients are responsible for ensuring compliance with third-party licenses.
            </p>
          </section>

          <section>
            <h2>7. Confidentiality</h2>
            <p>
              We respect the confidentiality of client information and project details. We will not disclose confidential information to third parties without client consent, except as required by law or to protect our rights.
            </p>
          </section>

          <section>
            <h2>8. Data Protection and Privacy</h2>
            <p>
              We are committed to protecting your privacy and personal data. Our collection, use, and processing of personal data is governed by our Privacy Policy, which is incorporated into these terms by reference. By using our services, you consent to our data practices as described in our Privacy Policy.
            </p>
            <p>
              We comply with applicable data protection laws, including the General Data Protection Regulation (GDPR) where applicable. You have certain rights regarding your personal data, as outlined in our Privacy Policy.
            </p>
          </section>

          <section>
            <h2>9. Cookies and Tracking</h2>
            <p>
              Our website uses cookies and similar tracking technologies to enhance your experience. By using our website, you consent to our use of cookies as described in our Privacy Policy. You can control cookie settings through your browser preferences.
            </p>
          </section>

          <section>
            <h2>10. Limitation of Liability</h2>
            <p>
              ShawnyMedia LLC shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, or use, arising out of or relating to our services. Our total liability shall not exceed the amount paid by you for the specific service giving rise to the claim.
            </p>
          </section>

          <section>
            <h2>11. Indemnification</h2>
            <p>
              Clients agree to indemnify and hold harmless ShawnyMedia LLC from any claims, damages, or expenses arising from client's use of our services or violation of these terms.
            </p>
          </section>

          <section>
            <h2>12. Termination</h2>
            <p>
              Either party may terminate services with written notice. Upon termination, clients are responsible for payment of work completed up to the termination date. We reserve the right to terminate services immediately for violations of these terms.
            </p>
          </section>

          <section>
            <h2>13. Dispute Resolution</h2>
            <p>
              Any disputes arising from these terms or our services will be resolved through good faith negotiation. If resolution cannot be reached, disputes will be resolved through appropriate legal channels in the courts of New York.
            </p>
          </section>

          <section>
            <h2>14. Force Majeure</h2>
            <p>
              We shall not be liable for any failure to perform our obligations due to circumstances beyond our reasonable control, including but not limited to natural disasters, government actions, or technical failures.
            </p>
          </section>

          <section>
            <h2>15. Severability</h2>
            <p>
              If any provision of these terms is found to be unenforceable or invalid, the remaining provisions will continue in full force and effect.
            </p>
          </section>

          <section>
            <h2>16. Changes to Terms</h2>
            <p>
              We reserve the right to modify these terms at any time. Changes will be effective immediately upon posting. Continued use of our services constitutes acceptance of modified terms. We will notify users of material changes via email or website notice.
            </p>
          </section>

          <section>
            <h2>17. Governing Law</h2>
            <p>
              These terms are governed by the laws of the State of New York, United States. Any legal proceedings shall be brought in the courts of New York. These terms constitute the entire agreement between you and ShawnyMedia LLC regarding our services.
            </p>
          </section>

          <section>
            <h2>18. Contact Information</h2>
            <p>
              For questions about these Terms of Service, please contact us at:
            </p>
            <div className="contact-info">
              <p><strong>Email:</strong> shawnysmedia@gmail.com</p>
              <p><strong>Address:</strong> Corning-Painted Post Area, NY</p>
              <p><strong>Response Time:</strong> We will respond to your inquiry within 48 hours</p>
            </div>
          </section>
        </div>

        <div className="legal-footer">
          <Link to="/" className="back-link">← Back to Home</Link>
        </div>
      </div>
    </div>
  );
};

export default Terms; 